import { StyleSheet, View } from 'react-native'
import { Card, Text, useTheme } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useCurrentOrg } from '@/hooks/useCurrentOrg'
import { useStartBreak } from '@/hooks/useStartBreak'
import { Button } from '@/ui/Button'
import { useForm, z, zodResolver } from '@/ui/form'
import { JobPicker } from '@/ui/time-entry/JobPicker'
import { LocationPicker } from '@/ui/time-entry/LocationPicker'

const startBreakSchema = z.object({
    jobId: z.string().min(1, 'Job is required'),
    locationId: z.string().optional(),
})

type StartBreakFormData = z.infer<typeof startBreakSchema>

interface StartBreakCardProps {
    orgSlug: string
}

export function StartBreakCard({ orgSlug }: StartBreakCardProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()
    const { org } = useCurrentOrg({ throwIfNotFound: false, onlySlug: orgSlug })
    const startBreakMutation = useStartBreak()

    const {
        control,
        handleSubmit,
        formState: { isValid },
        reset,
        watch,
    } = useForm<StartBreakFormData>({
        resolver: zodResolver(startBreakSchema),
        defaultValues: {
            jobId: '',
            locationId: '',
        },
        mode: 'onChange',
    })

    const jobId = watch('jobId')

    const onSubmit = (data: StartBreakFormData) => {
        if (!org?.id) return
        startBreakMutation.mutate(
            {
                parentEntry: {
                    job: data.jobId,
                    org: org.id,
                    location: data.locationId || undefined,
                },
            },
            { onSuccess: () => reset() }
        )
    }

    return (
        <Card style={styles.card}>
            <Card.Content>
                <Text
                    variant="bodyMedium"
                    style={[styles.subtitle, { color: theme.colors.onSurfaceVariant }]}
                >
                    Start a break without clocking in to a job timer
                </Text>

                <View style={styles.form}>
                    <JobPicker name="jobId" control={control} orgSlug={orgSlug} />
                    {jobId ? (
                        <LocationPicker name="locationId" control={control} jobId={jobId} />
                    ) : null}
                    <Button
                        title="Start Break"
                        mode="contained"
                        onPress={handleSubmit(onSubmit)}
                        disabled={!isValid || startBreakMutation.isPending}
                        loading={startBreakMutation.isPending}
                        icon="coffee-outline"
                        color={semanticColors.warning}
                        style={styles.button}
                    />
                </View>
            </Card.Content>
        </Card>
    )
}

const styles = StyleSheet.create({
    card: {
        elevation: 2,
    },
    subtitle: {
        marginBottom: 16,
    },
    form: {
        gap: 16,
    },
    button: {
        marginTop: 8,
        alignSelf: 'flex-end',
    },
})
